import { useCallback, useRef } from 'react'

/**
 * Keyboard travel across a board grid.
 *
 * The grids are tables of people against job columns (the day view) or against days (the
 * week and fortnight). Both mark every cell somebody could act on with `data-cell-key`, which
 * the notes drawer already used to find a cell from outside — so that attribute is the whole
 * contract here too: anything carrying it is a stop, and nothing else is.
 *
 * Roving focus, the way a spreadsheet does it. The TABLE is the one tab stop; landing on it
 * moves focus into the cell that last had it, or the first. Inside, the arrow keys move
 * between cells, Home and End run to the ends of the row, and Enter or Space does what a click
 * on the cell does — which is how the editor opens, so nothing about opening it is duplicated
 * here. Every cell is `tabIndex={-1}`: reachable by script and by a mouse click, and never
 * one of four hundred tab stops on a fortnight.
 *
 * Rows are the rows that hold cells, so a crew band is stepped over rather than landed on, and
 * a row with fewer cells (somebody off for the day, whose one cell spans the columns) takes
 * the nearest column it has rather than refusing the move.
 */

const CELL = '[data-cell-key]'
const rowsOf = (table) => [...table.querySelectorAll('tbody tr')].filter((tr) => tr.querySelector(CELL))
const cellsOf = (row) => [...row.querySelectorAll(CELL)]

// jsdom has no CSS.escape; a cell key holds ids, dates and colons, none of which need it, but
// a stray quote must not break the selector either way.
const attr = (key) => (globalThis.CSS?.escape ? CSS.escape(key) : String(key).replace(/["\\]/g, '\\$&'))

/** The cell drawn for a key, if it is on screen. */
export const findCell = (key, root = document) =>
  key ? root.querySelector(`[data-cell-key="${attr(key)}"]`) : null

/** Focus the cell for a key — where focus goes back to when an editor over it closes. */
export function focusCell(key) {
  findCell(key)?.focus()
}

/**
 * What Enter does to a cell: the click the mouse would have made. A week cell holding jobs
 * opens the first editable job, since that is what somebody sitting on the cell is looking at;
 * an empty one, and every day-grid cell, opens itself.
 */
export function activateCell(el) {
  const entry = el.querySelector('.week-entry.editable')
  ;(entry || el).click()
}

/**
 * @returns props to spread onto each `<table>` — or nothing at all when `enabled` is false,
 *   which is what the read-only wall passes so a screen nobody types at grows no tab stop.
 */
export function useGridKeys(enabled = true) {
  const lastKey = useRef(null)

  const onFocus = useCallback((e) => {
    const table = e.currentTarget
    if (e.target !== table) {
      // Focus landed on a cell, by click or by the arrows below. Remember it.
      const key = e.target.closest?.(CELL)?.dataset.cellKey
      if (key) lastKey.current = key
      return
    }
    // The table itself. From outside, that is Tab arriving: go to the remembered cell, or the
    // first. From inside, it is Shift+Tab on the way out, and redirecting it back in would be
    // a trap — so it is left alone and the next Shift+Tab leaves.
    if (table.contains(e.relatedTarget)) return
    const target = (lastKey.current && findCell(lastKey.current, table)) || table.querySelector(CELL)
    target?.focus()
  }, [])

  const onKeyDown = useCallback((e) => {
    const el = e.target.closest?.(CELL)
    if (!el) return
    const table = e.currentTarget
    const row = el.closest('tr')
    const cells = cellsOf(row)
    const col = cells.indexOf(el)
    let next = null
    switch (e.key) {
      case 'ArrowLeft':
        next = cells[col - 1]
        break
      case 'ArrowRight':
        next = cells[col + 1]
        break
      case 'Home':
        next = cells[0]
        break
      case 'End':
        next = cells[cells.length - 1]
        break
      case 'ArrowUp':
      case 'ArrowDown': {
        const rows = rowsOf(table)
        const target = rows[rows.indexOf(row) + (e.key === 'ArrowDown' ? 1 : -1)]
        if (target) {
          const targetCells = cellsOf(target)
          next = targetCells[Math.min(col, targetCells.length - 1)]
        }
        break
      }
      case 'Enter':
      case ' ':
        e.preventDefault()
        activateCell(el)
        return
      default:
        return
    }
    // Claimed even when there is nowhere to go, so the page under the board does not scroll
    // on the arrow that ran off its edge.
    e.preventDefault()
    next?.focus()
  }, [])

  return enabled ? { tabIndex: 0, onFocus, onKeyDown } : {}
}
